export type BroadcastBodySegment =
  | { type: 'text'; text: string }
  | { type: 'link'; href: string; label: string };

const LINK_PATTERN = '<link>\\s*([\\s\\S]*?)<\\/link>';

/** Only allow http(s) URLs to avoid javascript: etc. */
export function sanitizeBroadcastUrl(raw: string): string | null {
  const t = raw.trim();
  if (!/^https?:\/\//i.test(t)) return null;
  try {
    const u = new URL(t);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    return u.href;
  } catch {
    return null;
  }
}

export function defaultLinkLabel(href: string): string {
  try {
    const u = new URL(href);
    const host = u.hostname.replace(/^www\./i, '');
    let s = host + (u.pathname === '/' ? '' : u.pathname);
    if (u.search) s += u.search;
    if (s.length > 56) s = `${s.slice(0, 53)}…`;
    return s || href;
  } catch {
    return href.length > 56 ? `${href.slice(0, 53)}…` : href;
  }
}

function parseLinkInner(inner: string): { href: string; label: string } | null {
  const trimmed = inner.trim();
  if (!trimmed) return null;
  const lastPipe = trimmed.lastIndexOf('|');
  if (lastPipe > 0) {
    const labelPart = trimmed.slice(0, lastPipe).trim();
    const urlPart = trimmed.slice(lastPipe + 1).trim();
    const href = sanitizeBroadcastUrl(urlPart);
    if (href && labelPart) return { href, label: labelPart };
  }
  const href = sanitizeBroadcastUrl(trimmed);
  if (href) return { href, label: defaultLinkLabel(href) };
  return null;
}

/**
 * Parse broadcast body with optional tags: <link>https://...</link> or <link>Label|https://...</link>
 * (case-insensitive tag names). Other content stays plain text.
 */
export function parseBroadcastBodyToSegments(raw: string): BroadcastBodySegment[] {
  if (!raw) return [{ type: 'text', text: '' }];
  const segments: BroadcastBodySegment[] = [];
  let lastIndex = 0;
  const re = new RegExp(LINK_PATTERN, 'gi');
  let match: RegExpExecArray | null;
  while ((match = re.exec(raw)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', text: raw.slice(lastIndex, match.index) });
    }
    const inner = match[1] ?? '';
    const parsed = parseLinkInner(inner);
    if (parsed) {
      segments.push({ type: 'link', href: parsed.href, label: parsed.label });
    } else {
      segments.push({ type: 'text', text: match[0] });
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < raw.length) {
    segments.push({ type: 'text', text: raw.slice(lastIndex) });
  }
  if (segments.length === 0) return [{ type: 'text', text: raw }];
  return segments;
}
